import moment from "moment";
import React, { useEffect, useRef, useState } from "react";

import QuotesFirebaseDataService from "../../firebase/service/quotes.firebase.data.service";
import { transformQuotes } from "../../services/quotesService";
import HeaderPage from "../HeaderPage";
import Sidebar from "../Sidebar";
import FreightForm from "./components/forms/FreightForm";
import FreightList from "./components/table/FreightList";

const STORAGE_KEY = "quotes_engineering_state";

type QuoteItem = {
  id?: string;
  from_code: string;
  to_code: string;
  eta?: string; // "YYYY-MM-DD"
  etd?: string; // "YYYY-MM-DD"
  waiting_bot?: boolean;
  user_id?: string;
  type?: string;
  container_size?: string | number;
  [key: string]: any;
};

const QuotesEngineering: React.FC = () => {
  const [quotes, setQuotes] = useState<QuoteItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastSearchParams, setLastSearchParams] = useState<any>(null);

  // Guarda a REF da query p/ poder dar .off()
  const quotesRef = useRef<any>(null);
  const quotesImportedRef = useRef<any>(null);

  // Mapa estável dos registros atuais (evita recriar em cada render)
  const mapRef = useRef<Map<string, QuoteItem>>(new Map());

  // ------- Normalização + filtro por portos (range já vem da query) -------
  const normalizeAndFilter = (arr: QuoteItem[], value: any): QuoteItem[] => {
    return arr
      .map((item) => {
        const x = { ...item };
        if (x.container_size != null) {
          const num = String(x.container_size).replace(/\D/g, "");
          if (num === "22") x.container_size = "20";
          else if (num === "45") x.container_size = "40";
          else x.container_size = num;
        }
        return x;
      })
      .filter(
        (x) =>
          x.from_code === value.port_from_code &&
          x.to_code === value.port_to_code
      );
  };

  // ------- Aplica dedupe e escreve no estado -------
  const publish = (value: any) => {
    const userId = localStorage.getItem("user_id");
    const arr = Array.from(mapRef.current.values());
    const filtered = normalizeAndFilter(arr, value);
    // console.log("arr", filtered.filter((q) => q.import == true));

    const unique = Array.from(
      new Map(
        filtered.map((it) => [
          `${it.user_id}_${(it.etd || it.eta) ?? ""}_${it.type}_${it.container_size}`,
          it,
        ])
      ).values()
    );

    const quotesImported = unique.filter((q) => {
      if (!q.import) return true;

      const isUserQuote = q.user_id === userId;
      return isUserQuote;
    });
    setQuotes(transformQuotes(quotesImported));
  };

  // ------- Handlers de streaming -------
  const onChildAdded = (value: any) => (child: any) => {
    const obj: QuoteItem = { id: child.key, ...child.val() };
    mapRef.current.set(obj.id as string, obj);
    publish(value);
  };

  const onChildChanged = (value: any) => (child: any) => {
    const obj: QuoteItem = { id: child.key, ...child.val() };
    mapRef.current.set(obj.id as string, obj);
    publish(value);
  };

  const onChildRemoved = (value: any) => (child: any) => {
    const id = child.key as string;
    mapRef.current.delete(id);
    publish(value);
  };

  // ------- Submit do formulário -------
  const onFinish = async (value: any) => {
    console.log("Received values of form:", value);

    // Limpa listeners/estado anteriores
    if (quotesRef.current) {
      quotesRef.current.off();
      quotesRef.current = null;
    }
    if (quotesImportedRef.current) {
      quotesImportedRef.current.off();
      quotesImportedRef.current = null;
    }
    mapRef.current.clear();
    localStorage.removeItem(STORAGE_KEY);
    setQuotes([]);
    setLoading(true);
    setLastSearchParams(value);

    const fromCode = value.port_from_code;
    const toCode = value.port_to_code;
    const startDate = moment(value.date).format("YYYY-MM-DD");
    const endDate = moment(value.date).clone().add(45, "days").format("YYYY-MM-DD");

    try {
      // Busca quotes regulares (ETD)
      quotesRef.current = await QuotesFirebaseDataService.getByFromToEtdRangeRef(
        fromCode,
        toCode,
        startDate,
        endDate
      );

      // Busca quotes importados (ETA)
      quotesImportedRef.current = await QuotesFirebaseDataService.getByFromToEtaRangeRefImported(
        fromCode,
        toCode,
        startDate,
        endDate
      );

      let initializedCount = 0;
      const totalRefs = 2;

      const checkInitialization = () => {
        initializedCount++;
        if (initializedCount >= totalRefs) {
          setLoading(false);
        }
      };

      quotesRef.current.once("value", (snap: any) => {
        console.log("initial children (regular):", snap.numChildren());
        checkInitialization();
      });

      // Inicial: saber quantos existem nos quotes importados
      quotesImportedRef.current.once("value", (snap: any) => {
        console.log("initial children (imported):", snap.numChildren());
        checkInitialization();
      });

      // Tempo real para quotes regulares
      quotesRef.current.on("child_added", onChildAdded(value));
      quotesRef.current.on("child_changed", onChildChanged(value));
      quotesRef.current.on("child_removed", onChildRemoved(value));

      // Tempo real para quotes importados
      quotesImportedRef.current.on("child_added", onChildAdded(value));
      quotesImportedRef.current.on("child_changed", onChildChanged(value));
      quotesImportedRef.current.on("child_removed", onChildRemoved(value));

    } catch (err) {
      console.error("Erro na busca:", err);
      setLoading(false);
    }
  };

  // ------- Carrega cache (24h) na montagem -------
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const { quotes: savedQuotes, lastSearchParams: savedParams, timestamp } =
          JSON.parse(saved);
        const MAX_AGE = 24 * 60 * 60 * 1000;
        if (Date.now() - timestamp < MAX_AGE) {
          setQuotes(transformQuotes(savedQuotes));
          setLastSearchParams(savedParams);
        } else {
          localStorage.removeItem(STORAGE_KEY);
        }
      } catch (e) {
        console.error("Erro ao ler cache:", e);
        localStorage.removeItem(STORAGE_KEY);
      }
    }
    setLoading(false);
  }, []);

  // ------- Cleanup: remove todos os listeners -------
  useEffect(() => {
    return () => {
      if (quotesRef.current) {
        quotesRef.current.off();
      }
      if (quotesImportedRef.current) {
        quotesImportedRef.current.off();
      }
    };
  }, []);

  // ------- Persiste no cache quando quotes mudar -------
  useEffect(() => {
    if (!lastSearchParams) return;
    const storageQuotes = quotes.filter((q) => !q.waiting_bot);
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        quotes: storageQuotes,
        lastSearchParams,
        timestamp: Date.now(),
      })
    );
  }, [quotes, lastSearchParams]);

  return (
    <div className="flex-dashboard">
      <Sidebar elementoAtivo="quotesenginner" defaultCollapsed={true} />
      <main>
        <HeaderPage nomeOpcao="Cotação SPOT" />
        <div className="main-content">
          <FreightForm onFinish={onFinish} initialValues={lastSearchParams} />
          {/* Se o FreightList usar Ant Design Table, garanta rowKey="id" lá dentro */}
          <FreightList data={quotes} loading={loading} />
        </div>
      </main>
    </div>
  );
};

export default QuotesEngineering;
